import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MoveLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { ScanLinkChannel } from 'services/face-scan/type';
import { Button } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import { sendScanLink } from 'services/face-scan/api';

import WhatsappChat from 'assets/images/face-scan/whatsapp-scan-link.png';
import EmailScan from 'assets/images/face-scan/email-scan-link.png';
import { cn } from 'lib/utils';

interface SelectedMediaInfoProps {
  mode: ScanLinkChannel | undefined;
  onBack: () => void;
}

export default function SelectedMediaInfo({
  mode,
  onBack,
}: SelectedMediaInfoProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isSending, setIsSending] = useState<boolean>(false);

  let steps = [
    t('email_step_1'),
    t('email_step_2'),
    t('email_step_3'),
    t('email_step_4'),
    t('email_step_5'),
  ];
  if (mode === ScanLinkChannel.WHATSAPP) {
    steps = [
      t('whatsapp_step_1'),
      t('whatsapp_step_2'),
      t('whatsapp_step_3'),
      t('whatsapp_step_4'),
      t('whatsapp_step_5'),
    ];
  }
  if (mode === ScanLinkChannel.NOTIFICATION) {
    steps = [
      t('notification_step_1'),
      t('notification_step_2'),
      t('notification_step_3'),
      t('notification_step_4'),
    ];
  }

  const onResend = async () => {
    if (!mode) return;

    try {
      setIsSending(true);
      await sendScanLink(mode);
      toast.success(t('link_sent_msg'));
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className='space-y-12 min-h-[calc(100vh-7rem)] grid place-content-center max-w-3xl mx-auto py-8 relative'>
      <div className='absolute -left-20 top-8'>
        <Button variant='ghost' className='rounded-md' onClick={onBack}>
          <MoveLeft className='stroke-1 size-8' />
          <span className='sr-only'>Back</span>
        </Button>
      </div>

      <h1 className='text-lg font-semibold text-center'>
        {t('waiting_for_scan')}...
      </h1>

      <div className='grid items-center grid-cols-5 gap-x-12'>
        <div className='col-span-3'>
          <ul className='pl-4 space-y-4 list-decimal'>
            {steps.map((step, index) => (
              <li key={index} className='leading-tight'>
                {step}
              </li>
            ))}
          </ul>
        </div>

        <div className='flex flex-col items-center justify-center col-span-2 space-y-4'>
          <div
            className={cn(
              mode === ScanLinkChannel.WHATSAPP
                ? 'h-72 w-[250px]'
                : 'h-80 w-[200px]'
            )}
          >
            <img
              src={mode === ScanLinkChannel.WHATSAPP ? WhatsappChat : EmailScan}
              alt='icon'
              className='object-cover w-full h-full'
            />
          </div>
        </div>
      </div>

      <div className='flex flex-col items-center justify-center space-y-2'>
        <Button
          onClick={() => navigate(PATH_ROUTES.FACE_SCAN)}
          className='px-6 h-9 bg-medsi-blue hover:bg-medsi-blue/80'
          disabled={isSending}
        >
          {t('cancel')}
        </Button>
        <div className='flex items-center gap-x-2'>
          <Button variant='link' onClick={onResend} disabled={isSending}>
            {isSending && (
              <Loader2
                role='progressbar'
                className='mr-2 animate-spin size-4'
              />
            )}
            {t('click_here')}
          </Button>
          {t('send_link_again')}
        </div>
      </div>
    </div>
  );
}
